/* Submit to the existing HubSpot form, so its enrollment rules remain in effect. */
(function (root) {
  'use strict';

  const ENDPOINT = 'https://api.hsforms.com/submissions/v3/integration/submit/9191954/7e4add0b-6b91-4462-ba89-75a73f0c162e';
  const COMPANY_SIZES = ['1 - 9', '10 - 49', '50 - 249', '250 - 499', '500 - 999', '1000 - 1999', '2000+'];
  const FIELD_MAP = [
    ['firstname', '0-1', 'firstname'],
    ['lastname', '0-1', 'lastname'],
    ['phone', '0-1', 'phone'],
    ['email', '0-1', 'email'],
    ['0-2/domain', '0-2', 'domain'],
    ['0-2/company_size', '0-2', 'company_size'],
    ['message', '0-1', 'message']
  ];

  function buildPayload(values, context) {
    const get = (name) => String(values.get(name) || '').trim();
    if (!get('firstname') || !get('email') || !COMPANY_SIZES.includes(get('0-2/company_size')) || get('enquiry_extra')) {
      throw new Error('INVALID_FORM');
    }
    return {
      fields: FIELD_MAP.flatMap(([input, objectTypeId, name]) => {
        const value = get(input);
        return value ? [{ objectTypeId, name, value }] : [];
      }),
      context: context || {}
    };
  }

  function buildContext(pageUrl, pageName, cookie, analyticsAllowed) {
    const context = { pageName };
    try {
      const url = new URL(pageUrl);
      // Keep campaign attribution, but never forward arbitrary query-string data.
      const query = new URLSearchParams();
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((key) => {
        if (url.searchParams.has(key)) query.set(key, url.searchParams.get(key).slice(0, 200));
      });
      context.pageUri = url.origin + url.pathname + (query.size ? '?' + query.toString() : '');
    } catch (_) { /* A missing page URI must not prevent an enquiry. */ }
    if (analyticsAllowed) {
      const hutk = String(cookie || '').split(';').map((part) => part.trim()).find((part) => part.startsWith('hubspotutk='));
      if (hutk && /^[a-f0-9]{32}$/i.test(hutk.slice(11))) context.hutk = hutk.slice(11);
    }
    return context;
  }

  async function submitPayload(payload, fetcher, signal) {
    const response = await fetcher(ENDPOINT, {
      method: 'POST', mode: 'cors', credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), signal
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || (data.errors && data.errors.length)) {
      const error = new Error('SUBMISSION_FAILED');
      error.status = response.status;
      error.blockedEmail = Array.isArray(data.errors) && data.errors.some((item) => item.errorType === 'BLOCKED_EMAIL');
      throw error;
    }
    return data;
  }

  function errorMessage(error) {
    if (error.blockedEmail) return 'Please check your work email address and try again.';
    if (error.status === 429) return 'We’re receiving a lot of enquiries right now. Please wait a moment and try again.';
    return 'We couldn’t confirm your enquiry was received. Your answers are still here. Please try again, or email support@tapt.io.';
  }

  function readAnalyticsConsent(env) {
    const privacy = env.Shopify && env.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return null;
    try {
      const allowed = privacy.analyticsProcessingAllowed();
      return typeof allowed === 'boolean' ? allowed : null;
    } catch (_) { return null; }
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { ENDPOINT, COMPANY_SIZES, buildPayload, buildContext, submitPayload, errorMessage, readAnalyticsConsent };
  }
  if (!root.customElements || root.customElements.get('tapt-enquiry-form')) return;

  class TaptEnquiryForm extends root.HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.form = this.querySelector('[data-tapt-enquiry]');
      if (!this.form) return;
      this.initialized = true;
      this.button = this.querySelector('[data-enquiry-submit]');
      this.label = this.querySelector('[data-enquiry-button-label]');
      this.error = this.querySelector('[data-enquiry-error]');
      this.errorMessage = this.querySelector('[data-enquiry-error-message]');
      this.success = this.querySelector('[data-enquiry-success]');
      // JS failure leaves the submit button disabled; answers cannot end up in a URL.
      this.form.addEventListener('submit', (event) => this.handleSubmit(event));
      this.button.disabled = false;
    }

    async handleSubmit(event) {
      event.preventDefault();
      if (this.busy || this.completed || !this.form.reportValidity()) return;
      const values = new root.FormData(this.form);
      if (String(values.get('enquiry_extra') || '').trim()) return;
      this.busy = true;
      this.button.disabled = true;
      this.form.setAttribute('aria-busy', 'true');
      this.label.textContent = 'Sending…';
      this.error.hidden = true;
      let timeout;
      try {
        // A random event reference stays stable across retries; it is not sent to HubSpot.
        if (!this.enquiryId) this.enquiryId = root.crypto.randomUUID();
        const analyticsAllowed = readAnalyticsConsent(root);
        const context = buildContext(root.location.href, root.document.title, root.document.cookie, analyticsAllowed);
        const payload = buildPayload(values, context);
        if (this.dataset.preview === 'true') {
          if (this.dataset.previewOutcome === 'error') throw new Error('PREVIEW_ERROR');
          this.success.querySelector('[data-enquiry-success-message]').textContent = 'Preview complete. No enquiry was sent and no HubSpot record was created.';
        } else {
          const controller = new root.AbortController();
          timeout = root.setTimeout(() => controller.abort(), 20000);
          await submitPayload(payload, root.fetch.bind(root), controller.signal);
        }
        this.completed = true;
        this.form.hidden = true;
        this.success.hidden = false;
        this.success.focus();
        if (this.dataset.preview !== 'true') {
          // Retain existing conversion measurement; never pass form answers to pixels.
          try {
            if (root.Shopify && root.Shopify.analytics) {
              root.Shopify.analytics.publish('tapt:sales_inquiry_submitted', {
                lead_type: typeof root.taptLeadType === 'function' ? root.taptLeadType() : 'sales_enquiry',
                enquiry_id: this.enquiryId
              });
            }
          } catch (_) { /* Measurement must never turn a successful enquiry into an error. */ }
        }
      } catch (error) {
        (this.errorMessage || this.error).textContent = errorMessage(error);
        this.error.hidden = false;
        this.error.focus();
      } finally {
        root.clearTimeout(timeout);
        this.busy = false;
        this.button.disabled = false;
        this.form.removeAttribute('aria-busy');
        this.label.textContent = 'Send enquiry';
      }
    }
  }
  root.customElements.define('tapt-enquiry-form', TaptEnquiryForm);
})(typeof window !== 'undefined' ? window : {});
